import React, { useState, useEffect } from "react";
import { getProjection } from "../API's/Actioncreator";
// import Loader from "../../Common/Loader.component";
import Loader from "../../../../presentationals/Loader/Loader.component";
import IncomeBarChart from "../common/IncomeBarChart";
import VarianceCard from "../common/VarianceCard";
import ComponentBreakdownCards from "./components/ComponentBreakdownCards";
import VarianceSummaryTable from "./components/VarianceSummaryTable";
import InsightAlert from "./components/InsightAlert";
import moment from "moment";

function ProjectVSActual({ selectedMonth, commonFilter }) {
  const [projectionData, setProjectionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch projection data when selectedMonth changes
  useEffect(() => {
    if (selectedMonth && selectedMonth.length > 0) {
      setLoading(true);
      setError(null);

      const monthMap = {
        Jan: 0, Feb: 1, Mar: 2, Apr: 3,
        May: 4, Jun: 5, Jul: 6, Aug: 7,
        Sep: 8, Oct: 9, Nov: 10, Dec: 11
      };

      const sortedMonths = [...selectedMonth].sort((a, b) => {
        const [mA, yA] = a.split(" ");
        const [mB, yB] = b.split(" ");

        return new Date(yA, monthMap[mA]) - new Date(yB, monthMap[mB]);
      });

      const [startMonthName, startYear] = sortedMonths[0].split(" ");
      const [endMonthName, endYear] =
        sortedMonths[sortedMonths.length - 1].split(" ");

      const startMonthIndex = monthMap[startMonthName];
      const endMonthIndex = monthMap[endMonthName];

      // const startDate = `01-${String(startMonthIndex + 1).padStart(2, "0")}-${startYear}`;

      const lastDay = new Date(endYear, endMonthIndex + 1, 0).getDate();
      // const endDate = `${lastDay}-${String(endMonthIndex + 1).padStart(2, "0")}-${endYear}`;

      const startDate = moment(commonFilter?.fromDate, "YYYY-MM-DD").format("DD-MM-YYYY");
      const endDate = moment(commonFilter?.toDate, "YYYY-MM-DD").format("DD-MM-YYYY");

      // Fetch projection data
      new Promise((resolve, reject) => {
        getProjection(startDate, endDate, resolve, reject);
      })
        .then((response) => {
          setLoading(false);
          if (response.status === 200 && response.data) {
            setProjectionData(response.data);
          } else if (response.status === 500 || response.status === 404) {
            setError("Server error: " + (response.error?.errorMessage || "Unable to fetch projection data"));
          }
        })
        .catch((err) => {
          setLoading(false);
          setError("Failed to fetch projection data: " + err.message);
        });
    }
  }, [selectedMonth, commonFilter]);

  // Loading state
  if (loading) {
    return <Loader />;
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: '#EF4444',
        fontSize: '16px'
      }}>
        {error}
      </div>
    );
  }

  // Map API data to component structure
  const incomeVariance = projectionData?.incomeVariance;
  const collectionVariance = projectionData?.collectionVariance;
  const incomeComparison = projectionData?.incomeComparison;
  const collectionComparison = projectionData?.collectionComparison;
  const principal = projectionData?.principal;
  const interest = projectionData?.interest;
  const processingFee = projectionData?.processingFee;
  const varianceSummary = projectionData?.varianceSummary || [];
  const alerts = projectionData?.alerts || [];

  return (
    <>
      <div style={{ marginTop: "40px" }}>
        <h4 style={{ color: "#0A0A0A", fontWeight: "600", fontSize: "20px" }}>
          Projection vs Actual Analysis{" "}
        </h4>
        <p style={{ color: "#4A5565", fontSize: "14px", fontWeight: "400" }}>
          {selectedMonth?.length > 0
            ? `Month: ${selectedMonth?.join(", ")}`
            : "No months selected"}
        </p>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "30px",
          marginTop: "20px",
        }}
      >
        <VarianceCard
          title={incomeVariance?.label || "Income Variance"}
          amount={incomeVariance?.amountDisplay || "₹0"}
          originalValue={incomeVariance?.amount || 0}
          subtitle={incomeVariance?.directionLabel || "0%"}
          bgColor={incomeVariance?.direction === "UP" ? "#D8EBDE" : "#F9E1E3"}
          textColor={incomeVariance?.direction === "UP" ? "#327E45" : "#E84E4E"}
          icon={incomeVariance?.direction === "UP" ? "up" : "down"}
        />

        <VarianceCard
          title={collectionVariance?.label || "Collection Variance"}
          amount={collectionVariance?.amountDisplay || "₹0"}
          originalValue={collectionVariance?.amount || 0}
          subtitle={collectionVariance?.directionLabel || "0%"}
          bgColor={collectionVariance?.direction === "UP" ? "#D8EBDE" : "#F9E1E3"}
          textColor={collectionVariance?.direction === "UP" ? "#327E45" : "#E84E4E"}
          icon={collectionVariance?.direction === "UP" ? "up" : "down"}
        />
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "14px",
          padding: "20px",
          boxShadow:
            "0px 1px 2px rgba(0,0,0,0.10), 0px 1px 3px rgba(0,0,0,0.10)",
          marginTop: "40px",
        }}
      >
        {incomeComparison && collectionComparison && (
          <IncomeBarChart
            title="Income & Collection Comparison"
            categories={["Income", "Collection"]}
            series={[
              {
                name: "Projected",
                data: [
                  (incomeComparison?.projected / 10000000).toFixed(2),
                  (collectionComparison?.projected / 10000000).toFixed(2),
                ],
              },
              {
                name: "Actual",
                data: [
                  (incomeComparison?.actual / 10000000).toFixed(2),
                  (collectionComparison?.actual / 10000000).toFixed(2),
                ],
              },
            ]}
            yType="currency"
            gridType="horizontal"
            colors={["#94a3b8", "#10b981"]}
            showLegend={true}
          />
        )}
      </div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "20px",
          background: "#ffffff",
          marginTop: "40px",

          boxShadow:
            "0px 1px 2px rgba(0,0,0,0.10), 0px 1px 3px rgba(0,0,0,0.10)",
        }}
      >
        <ComponentBreakdownCards principal={principal} interest={interest} processingFee={processingFee} />

        {principal && interest && processingFee && (
          <div style={{ marginTop: "20px" }}>
            <IncomeBarChart
              categories={["Principal", "Interest", "Processing Fee"]}
              gridType="horizontal"
              series={[
                {
                  name: "Projected",
                  data: [
                    (principal?.projected / 10000000).toFixed(2),
                    (interest?.projected / 10000000).toFixed(2),
                    (processingFee?.projected / 10000000).toFixed(2),
                  ],
                },

                {
                  name: "Actual",
                  data: [
                    (principal?.actual / 10000000).toFixed(2),
                    (interest?.actual / 10000000).toFixed(2),
                    (processingFee?.actual / 10000000).toFixed(2),
                  ],
                },
              ]}
              colors={["#94a3b8", "#10b981"]}
              showLegend={true}
            />
          </div>
        )}
      </div>
      <VarianceSummaryTable varianceSummary={varianceSummary} />
      <InsightAlert alerts={alerts} />
    </>
  );
}

export default ProjectVSActual;
